import { createReduxEnhancer } from '@sentry/react'
import { applyMiddleware, createStore, Middleware } from 'redux'
import { composeWithDevTools } from 'redux-devtools-extension'
import { createEpicMiddleware } from 'redux-observable'
import {
  createStateSyncMiddleware,
  initMessageListener
} from 'redux-state-sync'

import { isOpenFinRuntime } from '../containers/OpenFin/helpers'
import { deepMerge, HydrationPayload } from '../helpers/hydration'

import activityLogEpic from './activityLog/epic'
import activityTickerEpic from './admin/activityTicker/epic'
import aggressAttemptEpic from './admin/aggressAttempt/epic'
import bbmIsinEpic from './admin/bbmisin/epic'
import externalLiquidityStatsEpic from './admin/externalLiquidityStats/epic'
import managementReportsEpic from './admin/managementReports/epic'
import queueStatsEpic from './admin/queueStats/epic'
import restrictedTradesEpic from './admin/restrictedTrades/epic'
import systemParamEpic from './admin/sysparam/epic'
import tickerRatingEpic from './admin/tickerRating/epic'
import tiersEpic from './admin/tiers/epic'
import userDashboardEpic from './admin/userDashboard/epic'
import userDataEpic from './admin/userData/epic'
import venueEpic from './admin/venues/epic'
import tradeBlotterEpic from './tradeBlotter/epic'

import alertFiltersEpic from './alertFilter/epic'
import alertsEpic from './alerts/epic'
import reducer from './appReducer'
import authEpic from './auth/epic'
import benchmarkDataEpic from './benchmarkData/epic'
import booksEpic from './books/epic'
import checkedOrdersEpic from './checkedOrders/epic'
import customersEpic from './customers/epic'
import depthOfMarketEpic from './depthOfMarket/epic'
import gridEpic from './grid/epic'
import heartbeatEpic from './heartbeat/epic'
import lastLookEpic from './lastLook/epic'
import listTradingEpic from './listTrading/epic'
import logEpic from './log/epic'
import marketEpic from './market/epic'
import orderEpic from './order/epic'
import passiveOrdersEpic from './passiveOrders/epic'
import rfqEpic from './rfq/epic'
import { safeCombineEpics } from './safeCombineEpics'
import searchSecuritiesEpic from './searchSecurities/epic'
import securitiesEpic from './securities/epic'
import settingsEpic from './settings/epic'
import stagedOrdersEpic from './stagedOrders/epic'
import orderToastEpic from './toasts/epic'
import { State, StoreAction } from './types'
import uploadOrdersEpic from './upload/epic'
import userEditorDataEpic from './userEditorData/epic'
import userPreferencesEpic from './userPreferences/epic'
import usersEpic from './users/epic'
import watchListEpic from './watchList/epic'
import webSettingsEpic from './webSettings/epic'
import windowsEpic from './windows/epic'
import wsEpic from './ws/epic'

const rootEpic = safeCombineEpics(
  authEpic,
  activityLogEpic,
  tradeBlotterEpic,
  aggressAttemptEpic,
  benchmarkDataEpic,
  depthOfMarketEpic,
  securitiesEpic,
  windowsEpic,
  orderEpic,
  wsEpic,
  lastLookEpic,
  watchListEpic,
  checkedOrdersEpic,
  stagedOrdersEpic,
  searchSecuritiesEpic,
  userPreferencesEpic,
  settingsEpic,
  booksEpic,
  alertsEpic,
  uploadOrdersEpic,
  marketEpic,
  logEpic,
  heartbeatEpic,
  webSettingsEpic,
  usersEpic,
  customersEpic,
  tiersEpic,
  userDataEpic,
  queueStatsEpic,
  gridEpic,
  alertFiltersEpic,
  systemParamEpic,
  bbmIsinEpic,
  tickerRatingEpic,
  managementReportsEpic,
  userEditorDataEpic,
  activityTickerEpic,
  userDashboardEpic,
  restrictedTradesEpic,
  passiveOrdersEpic,
  rfqEpic,
  venueEpic,
  externalLiquidityStatsEpic,
  listTradingEpic,
  orderToastEpic
)

const whitelistActions = [
  'benchmarkdata.setSecuritySearch',
  'depthofmarket.closePopout',
  'depthofmarket.closeAllPopouts',
  'depthofmarket.popout',
  'depthofmarket.updatePopouts',
  'listTrading.setListTradingTransactionId',
  'rfq.submitRfq',
  'securities.setQueryFilter',
  'securities.createGrid',
  'securities.removeGrid',
  'securities.showSecurityDetails',
  'tradeBlotter.fetchCancel',
  'userPreferences.fetchUserPreferencesSuccess',
  'userPreferences.setUserPreferencesSuccess',
  'users.setSelectedUser',
  'watchList.cancelAllWatchListOrdersAction',
  'windowFrame.setActiveMenu',
  'windows.removeLastLookFromWindows',
  'windows.removeAggressorWindow',
  'windows.removeRfqWindow',
  'windows.hideRfqWindow',
  'windows.cancelRfq'
]
const config = {
  predicate: (action: { type: string }) =>
    whitelistActions.includes(action.type)
}

const epicMiddleware = createEpicMiddleware()
const middlewares: Middleware[] = [epicMiddleware]

const isOpenFin = isOpenFinRuntime()
isOpenFin && middlewares.push(createStateSyncMiddleware(config))

const sentryReduxEnhancer = createReduxEnhancer({})

const getStore = (hydrate?: HydrationPayload) => {
  /* Overlay onto the full default tree so a partial slice keeps its untouched keys
     (a reducer only falls back to its initialState when its whole slice is undefined). */
  const preloadedState = hydrate
    ? deepMerge(
        reducer(undefined, {
          type: '@@hydrate/init'
        } as unknown as StoreAction),
        hydrate
      )
    : undefined
  const store = createStore<State, StoreAction, unknown, unknown>(
    reducer,
    preloadedState,
    composeWithDevTools(applyMiddleware(...middlewares), sentryReduxEnhancer)
  )

  /*
    Views and the WindowFrame window listen for synced actions, which deliver
    live theme toggles. The WindowFrame has no WS stream to fetch prefs itself,
    so its boot theme is seeded from localStorage (see helpers/theme).
  */
  const isWindowFrame = window.location.pathname === '/WindowFrame'
  isOpenFin && (fin.me.isView || isWindowFrame) && initMessageListener(store)
  epicMiddleware.run(rootEpic)

  return store
}

export default getStore
